/**
 * OrbCanvas — Northstar composition scene.
 *
 * Stage 3 Refinement (2026-02):
 *   Historically this file positioned each emblem with individual
 *   translations. The refinement pass replaces those ad-hoc offsets
 *   with a real grid system:
 *
 *   - A single central axis (viewport center) anchors the portrait,
 *     rings, matrix field and both domain columns.
 *   - Two column primitives (<LeftDomainField/>, <RightDomainField/>)
 *     render five DomainRow entries each, sharing exact row Y's, exact
 *     emblem X, exact emblem-text gap, exact textMaxW — no per-item
 *     drift.
 *   - Domain-row text is mirrored: the left column right-aligns and
 *     extends leftward from the emblem; the right column left-aligns
 *     and extends rightward.
 *   - Every spatial value comes from `layoutTokens.js` so tweaks are
 *     centrally maintainable.
 *
 * Historical name + test-ids preserved so existing selectors and
 * regression tests continue to resolve.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ORBS } from "../../constants/northstarOrbs";
import CategoryEmblem from "./emblems";
import MatrixField from "./environment/MatrixField";
import DigitalRingSystem from "./environment/DigitalRingSystem";
import ConnectionNetwork from "./environment/ConnectionNetwork";
import MicroField from "./environment/MicroField";

export default function OrbCanvas({
  centerSize = 320,
  orbSize = 88,
  columnX = 420,
  rowYs = [-336, -168, 0, 168, 336],
  emblemTextGap = 22,
  textMaxW = 220,
  nameToDescGap = 4,
  nameSize = "text-lg",
  descSize = "text-sm",
  descAtRest = "always",
  labelStack = "beside",
  tier = "desktop-lg",
  matrixInner = 0.22,
  matrixOuter = 0.48,
  onOpen,
}) {
  const navigate = useNavigate();
  const [previewing, setPreviewing] = useState(null);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [opening, setOpening] = useState(null);
  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const openCategory = (orb) => {
    if (onOpen) return onOpen(orb);
    if (orb.connected && orb.route) {
      setOpening(orb);
      try { sessionStorage.setItem("northstar:origin", orb.key); } catch {}
      const sep = orb.route.includes("?") ? "&" : "?";
      setTimeout(
        () => navigate(orb.route + sep + "from=northstar&orb=" + orb.key),
        550
      );
    } else {
      setPreviewing(orb);
    }
  };

  const leftDomains = ORBS.slice(0, 5);
  const rightDomains = ORBS.slice(5, 10);

  const rowConfig = {
    columnX,
    rowYs,
    orbSize,
    emblemTextGap,
    textMaxW,
    nameToDescGap,
    nameSize,
    descSize,
    descAtRest,
    labelStack,
  };

  return (
    <div
      className="relative w-full h-full"
      data-testid="orb-canvas"
      data-layout="stage3-composed"
      data-tier={tier}
      data-reduced-motion={reducedMotion ? "true" : "false"}
    >
      {/* SR-only heading so assistive tech announces the group's
          purpose ("Ten Northstar pathways") before the buttons are
          traversed. Visual layout is unchanged. */}
      <h2 className="sr-only" id="northstar-pathways-heading">
        Ten Northstar support pathways
      </h2>

      {/* Layer 01 — Matrix / digital-data atmosphere. Outer-weighted
          attenuation. Fills the full viewport behind everything else. */}
      <MatrixField
        reducedMotion={reducedMotion}
        innerRatio={matrixInner}
        outerRatio={matrixOuter}
      />

      {/* Layer 02 — Organic emerald atmosphere. Deliberately NOT a
          single perfect radial disc. Uses two elliptical + one
          upward-offset radial gradient so the illumination wraps
          around the shoulders and dissipates asymmetrically. */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 pointer-events-none"
        style={{ transform: "translate(-50%, -50%)", zIndex: 2 }}
        data-testid="northstar-central-atmosphere"
      >
        {/* Distant nebula — wide, deep forest ellipse that extends
             BEYOND the outermost ring so the atmosphere reads as
             organically wrapping the composition, not as another
             concentric circle. */}
        <div
          className="absolute left-1/2 top-1/2 rounded-full"
          style={{
            transform: "translate(-50%, -46%)",
            width: centerSize * 8.2,
            height: centerSize * 5.6,
            background:
              "radial-gradient(ellipse 60% 62% at 50% 50%, rgba(20,72,52,0.24) 0%, rgba(12,54,42,0.12) 44%, rgba(4,8,22,0) 78%)",
            filter: "blur(30px)",
          }}
        />
        {/* Mid-field emerald — offset upward, elliptical, extended
             horizontally so illumination wraps behind the shoulders. */}
        <div
          className="absolute left-1/2 top-1/2 rounded-full"
          style={{
            transform: "translate(-50%, -52%)",
            width: centerSize * 4.4,
            height: centerSize * 3.2,
            background:
              "radial-gradient(ellipse 66% 60% at 48% 44%, rgba(56,168,116,0.40) 0%, rgba(30,102,72,0.22) 52%, rgba(4,8,22,0) 84%)",
            filter: "blur(16px)",
          }}
        />
        {/* Under-shoulder wash — a broad horizontal ellipse below
             the head; suggests light dispersing into the deep dark. */}
        <div
          className="absolute left-1/2 top-1/2 rounded-full"
          style={{
            transform: "translate(-50%, -28%)",
            width: centerSize * 3.2,
            height: centerSize * 1.8,
            background:
              "radial-gradient(ellipse 66% 42% at 50% 60%, rgba(126,212,147,0.36) 0%, rgba(64,168,118,0.18) 55%, rgba(4,8,22,0) 92%)",
            filter: "blur(10px)",
          }}
        />
        {/* Head-light — soft mint highlight above the face (very subtle) */}
        <div
          className="absolute left-1/2 top-1/2 rounded-full"
          style={{
            transform: "translate(-52%, -70%)",
            width: centerSize * 1.6,
            height: centerSize * 1.15,
            background:
              "radial-gradient(ellipse 62% 55% at 50% 44%, rgba(184,239,198,0.16) 0%, rgba(126,212,147,0.06) 55%, rgba(4,8,22,0) 92%)",
            filter: "blur(8px)",
          }}
        />
      </div>

      {/* Layer 03 — Concentric digital ring architecture. Sized off the
          portrait so the whole environment scales as one unit. */}
      <DigitalRingSystem
        identity={centerSize}
        tier={tier}
        reducedMotion={reducedMotion}
      />

      {/* Layer 03b — MicroField. Sparse deterministic depth-fragments
          (tiny nodes, micro-arcs, irregular ticks, dust) between the
          portrait halo and R2. Gives the central field discoverable
          depth without adding brightness. */}
      <MicroField identity={centerSize} />

      {/* Layer 04 — Radial connection network. Stage 4: the network
          reacts to the hovered/focused emblem by brightening that
          single pathway. All other lines remain at idle. */}
      <ConnectionNetwork
        identity={centerSize}
        orbSize={orbSize}
        columnX={columnX}
        rowYs={rowYs}
        tier={tier}
        hoveredKey={hoveredKey}
        reducedMotion={reducedMotion}
      />

      {/* Layer 05 — Two mirrored domain columns. Every emblem in a
          column shares the same X anchor; every row-pair (left+right)
          shares the same Y. */}
      <LeftDomainField
        domains={leftDomains}
        cfg={rowConfig}
        hoveredKey={hoveredKey}
        setHoveredKey={setHoveredKey}
        onActivate={openCategory}
      />
      <RightDomainField
        domains={rightDomains}
        cfg={rowConfig}
        hoveredKey={hoveredKey}
        setHoveredKey={setHoveredKey}
        onActivate={openCategory}
      />

      {previewing && (
        <ComingSoonPortal
          orb={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}

      {opening && (
        <div
          aria-hidden="true"
          data-testid="orb-opening-transition"
          className="fixed inset-0 pointer-events-none"
          style={{
            background: opening.connected
              ? "radial-gradient(circle at 50% 50%, rgba(126,212,147,0.30), rgba(4,8,22,0.94) 70%)"
              : "radial-gradient(circle at 50% 50%, rgba(148,163,184,0.22), rgba(4,8,22,0.94) 70%)",
            opacity: 1,
            animation: "orb-open-fade 550ms ease-in forwards",
            zIndex: 300,
          }}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
            <p className="text-xs uppercase tracking-[0.4em] text-[#B8EFC6] mb-2">
              Opening
            </p>
            <p className="text-2xl font-semibold tracking-tight">
              {opening.label}
            </p>
          </div>
          <style>{`@keyframes orb-open-fade { 0% { opacity: 0; } 40% { opacity: 0.6; } 100% { opacity: 1; } }`}</style>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Domain columns                                                       */
/* -------------------------------------------------------------------- */

function LeftDomainField({ domains, cfg, hoveredKey, setHoveredKey, onActivate }) {
  return (
    <div
      className="absolute inset-0 pointer-events-none"
      role="list"
      aria-labelledby="northstar-pathways-heading"
      data-testid="northstar-left-column"
    >
      {domains.map((orb, i) => (
        <DomainRow
          key={orb.key}
          orb={orb}
          side="left"
          rowIndex={i}
          cfg={cfg}
          hovered={hoveredKey === orb.key}
          onHoverStart={() => setHoveredKey(orb.key)}
          onHoverEnd={() =>
            setHoveredKey((k) => (k === orb.key ? null : k))
          }
          onActivate={() => onActivate(orb)}
        />
      ))}
    </div>
  );
}

function RightDomainField({ domains, cfg, hoveredKey, setHoveredKey, onActivate }) {
  return (
    <div
      className="absolute inset-0 pointer-events-none"
      role="list"
      aria-labelledby="northstar-pathways-heading"
      data-testid="northstar-right-column"
    >
      {domains.map((orb, i) => (
        <DomainRow
          key={orb.key}
          orb={orb}
          side="right"
          rowIndex={i}
          cfg={cfg}
          hovered={hoveredKey === orb.key}
          onHoverStart={() => setHoveredKey(orb.key)}
          onHoverEnd={() =>
            setHoveredKey((k) => (k === orb.key ? null : k))
          }
          onActivate={() => onActivate(orb)}
        />
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* DomainRow — one composed row: emblem + text with mirrored alignment  */
/* -------------------------------------------------------------------- */

function DomainRow({
  orb, side, rowIndex, cfg, hovered, onHoverStart, onHoverEnd, onActivate,
}) {
  const isLeft = side === "left";
  const {
    columnX, rowYs, orbSize, emblemTextGap, textMaxW,
    nameToDescGap, nameSize, descSize, descAtRest, labelStack,
  } = cfg;

  const y = rowYs[rowIndex];
  const x = isLeft ? -columnX : columnX;
  const showDesc = descAtRest === "always" || hovered;
  const stackBelow = labelStack === "below";

  return (
    <div
      className="absolute left-1/2 top-1/2 pointer-events-auto"
      style={{
        transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
        zIndex: hovered ? 20 : 10,
      }}
      role="listitem"
      data-testid={`orb-${orb.key}`}
      data-connected={orb.connected ? "true" : "false"}
      data-hovered={hovered ? "true" : "false"}
      data-side={side}
      data-row={rowIndex}
    >
      <div
        className={
          stackBelow
            ? "flex flex-col items-center text-center"
            : `flex items-center ${
                isLeft ? "flex-row-reverse text-right" : "flex-row text-left"
              }`
        }
        style={{
          gap: stackBelow ? 6 : emblemTextGap,
        }}
      >
        <EmblemButton
          orb={orb}
          size={orbSize}
          hovered={hovered}
          onHoverStart={onHoverStart}
          onHoverEnd={onHoverEnd}
          onActivate={onActivate}
        />
        <DomainText
          orb={orb}
          isLeft={isLeft}
          stackBelow={stackBelow}
          textMaxW={textMaxW}
          nameToDescGap={nameToDescGap}
          nameSize={nameSize}
          descSize={descSize}
          showDesc={showDesc}
          hovered={hovered}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* EmblemButton                                                         */
/* -------------------------------------------------------------------- */

function EmblemButton({ orb, size, hovered, onHoverStart, onHoverEnd, onActivate }) {
  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const trans = reducedMotion ? "none" : undefined;
  const [pressed, setPressed] = React.useState(false);

  return (
    <button
      type="button"
      aria-labelledby={`orb-name-${orb.key}`}
      aria-describedby={`orb-desc-${orb.key} orb-status-${orb.key}`}
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setPressed(true);
          setTimeout(() => setPressed(false), 160);
          onActivate();
        }
      }}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      onFocus={onHoverStart}
      onBlur={onHoverEnd}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      className="relative focus:outline-none group flex-shrink-0"
      style={{
        width: size,
        height: size,
        // Ensure the touch target is at least 44×44 CSS px (WCAG 2.5.5).
        // A pseudo-hitbox is not needed because size already exceeds
        // that at every tier (48 → 86 px).
        transform: reducedMotion
          ? undefined
          : hovered
          ? pressed ? "scale(0.97)" : "scale(1.045)"
          : "scale(1)",
        transition: trans || "transform 220ms ease-out",
      }}
      data-testid={`orb-emblem-btn-${orb.key}`}
      data-hover-state={hovered ? "hovered" : "idle"}
      data-pressed={pressed ? "true" : "false"}
    >
      {/* Soft outer glow — restrained at rest, brightens on hover */}
      <div
        aria-hidden="true"
        className="absolute inset-[-14px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(126,212,147,0.42), transparent 70%)",
          opacity: hovered ? 1 : 0.35,
          transition: trans || "opacity 260ms ease-out",
          filter: "blur(7px)",
        }}
      />

      {/* Thin luminous ring — the flat badge frame */}
      <div
        className="relative w-full h-full rounded-full"
        style={{
          border: `1.5px solid ${
            hovered
              ? "rgba(223,247,230,1)"
              : orb.connected
              ? "rgba(184,239,198,0.85)"
              : "rgba(184,239,198,0.55)"
          }`,
          background: hovered
            ? "radial-gradient(circle, rgba(20,72,52,0.55) 0%, rgba(6,32,22,0.30) 65%, rgba(4,8,22,0) 100%)"
            : "radial-gradient(circle, rgba(4,8,22,0.55) 0%, rgba(4,8,22,0.15) 65%, rgba(4,8,22,0) 100%)",
          boxShadow: hovered
            ? "0 0 32px rgba(126,212,147,0.60), inset 0 0 14px rgba(126,212,147,0.24)"
            : "0 0 12px rgba(126,212,147,0.25), inset 0 0 8px rgba(126,212,147,0.10)",
          transition: trans || "box-shadow 300ms ease-out, border-color 300ms ease-out, background 300ms ease-out",
        }}
      >
        <div
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 pointer-events-none"
          style={{ transform: "translate(-50%, -50%)" }}
          data-testid={`orb-emblem-${orb.key}`}
        >
          <CategoryEmblem
            orbKey={orb.key}
            size={Math.round(size * 0.55)}
          />
        </div>
      </div>

      {/* Focus ring (keyboard) — dual-ring for high contrast on
          dark backgrounds. Visible only via :focus-visible so mouse
          clicks don't leave a stuck ring. */}
      <span
        aria-hidden="true"
        className="absolute inset-[-6px] rounded-full pointer-events-none opacity-0 group-focus-visible:opacity-100"
        style={{
          boxShadow: "0 0 0 2px #7ED493, 0 0 0 6px #040816, 0 0 0 8px rgba(126,212,147,0.35)",
          transition: trans || "opacity 120ms ease-out",
        }}
      />
    </button>
  );
}

/* -------------------------------------------------------------------- */
/* DomainText                                                           */
/* -------------------------------------------------------------------- */

function DomainText({
  orb, isLeft, stackBelow, textMaxW, nameToDescGap,
  nameSize, descSize, showDesc, hovered,
}) {
  return (
    <div
      className="flex flex-col"
      style={{
        width: stackBelow ? "auto" : textMaxW,
        maxWidth: textMaxW,
        alignItems: stackBelow
          ? "center"
          : isLeft
          ? "flex-end"
          : "flex-start",
      }}
      data-testid={`orb-label-${orb.key}`}
      data-hover-state={hovered ? "hovered" : "idle"}
    >
      <span
        id={`orb-name-${orb.key}`}
        className={`${nameSize} tracking-tight`}
        style={{
          fontWeight: hovered ? 700 : 600,
          textDecorationLine: hovered ? "underline" : "none",
          textDecorationColor: "rgba(184,239,198,0.55)",
          textDecorationThickness: "1px",
          textUnderlineOffset: "3px",
          color: hovered
            ? "#F0FBF4"
            : orb.connected
            ? "#DFF7E6"
            : "rgba(232,238,245,0.94)",
          textShadow: "0 1px 8px rgba(4,8,22,0.95)",
          lineHeight: 1.15,
          transition: "color 220ms ease-out",
        }}
      >
        {orb.label}
      </span>
      {showDesc && (
        <span
          id={`orb-desc-${orb.key}`}
          className={`${descSize} leading-snug`}
          style={{
            marginTop: nameToDescGap,
            color: hovered
              ? "rgba(220,247,225,0.95)"
              : orb.connected
              ? "rgba(200,240,215,0.85)"
              : "rgba(210,220,232,0.78)",
            textShadow: "0 1px 4px rgba(4,8,22,0.9)",
            maxWidth: textMaxW,
            transition: "color 220ms ease-out",
          }}
          data-testid={`orb-desc-${orb.key}`}
        >
          {orb.description}
        </span>
      )}
      {/* Description is duplicated as a visually-hidden node when
          collapsed on `focus-only` tiers so assistive tech always
          exposes the description regardless of the visual state. */}
      {!showDesc && (
        <span
          id={`orb-desc-${orb.key}`}
          className="sr-only"
        >
          {orb.description}
        </span>
      )}
      {/* Availability status — visible on hover/focus + always in
          the accessibility tree via `orb-status-<key>`. */}
      <span
        id={`orb-status-${orb.key}`}
        className="text-[11px] tracking-wide whitespace-nowrap"
        style={{
          marginTop: 4,
          color: orb.connected
            ? "rgba(126,212,147,0.9)"
            : "rgba(203,213,225,0.72)",
          textShadow: "0 1px 4px rgba(4,8,22,0.9)",
          opacity: hovered ? 1 : 0,
          // Reserve height so `orb-status` visibility toggles do
          // not shift layout.
          height: hovered ? "auto" : 0,
          overflow: "hidden",
          transition: "opacity 200ms ease-out",
        }}
      >
        {orb.connected ? "Open. Press enter to navigate." : "Coming soon."}
      </span>
      {!hovered && (
        <span className="sr-only">
          {orb.connected ? "Available." : "Coming soon."}
        </span>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* ComingSoonPortal                                                     */
/* -------------------------------------------------------------------- */

function ComingSoonPortal({ orb, onClose }) {
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${orb.label} — coming soon`}
      onClick={onClose}
      data-testid="orb-coming-soon-modal"
      style={{
        background:
          "radial-gradient(ellipse at center, rgba(4,8,22,0.4) 0%, rgba(4,8,22,0.88) 60%)",
        backdropFilter: "blur(8px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-md w-full text-center px-8 py-10"
      >
        <div className="mx-auto mb-6 relative w-24 h-24">
          <div className="absolute inset-[-14px] rounded-full blur-2xl bg-[radial-gradient(circle,rgba(126,212,147,0.30),transparent_70%)]" />
          <div
            className="relative w-full h-full rounded-full"
            style={{
              border: "1.5px solid rgba(184,239,198,0.7)",
              background:
                "radial-gradient(circle, rgba(4,8,22,0.55) 0%, rgba(4,8,22,0.15) 65%, rgba(4,8,22,0) 100%)",
            }}
          />
        </div>
        <p className="text-xs uppercase tracking-[0.35em] text-white/70 mb-3">
          {orb.label}
        </p>
        <p className="text-white/75 text-sm mb-3">{orb.description}</p>
        <h3 className="text-2xl font-semibold text-white mb-3 tracking-tight">
          Not yet in your Northstar.
        </h3>
        <p className="text-white/80 text-base leading-relaxed mb-8">
          We&rsquo;re preparing this part of your journey with real partners.
          It will appear here when it&rsquo;s ready &mdash; not before.
        </p>
        <button
          onClick={onClose}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-white/25 text-white text-sm tracking-wide hover:bg-white/5 hover:border-[#7ED493]/60 transition-colors"
          data-testid="orb-coming-soon-close"
        >
          Return to Northstar
        </button>
      </div>
    </div>
  );
}
